/* eslint-disable camelcase */
import { createSelector } from 'reselect';
import _ from 'lodash';
import moment from 'moment';
import translations from 'translations';
import configSelector from './config';

export const selectServices = createSelector(
  (state) => state.profile,
  configSelector,
  (profile, { languageCode }) => {
    const { payload, isGetWaiting } = profile;
    const { abilities, customer_type, onboard, company, country } = payload;
    const services = [];
    let mandatoryStepsFulfilled = true;

    const CONST_SHUFFLE = 'SHUFFLE_';

    if (!abilities || !onboard || isGetWaiting) return { services, isWaiting: isGetWaiting };

    const { steps } = onboard;

    const dashAgreement = _.find(steps, { name: 'dash_accepted_agreement' });

    steps
      .filter((step) => step.requirement === 'mandatory')
      .forEach((step) => {
        if (!step.fulfilled) {
          mandatoryStepsFulfilled = false;
        }
      });

    let servicesObj = {};
    abilities.forEach((ability) => {
      const {
        visible,
        platform: { name: platformName },
      } = ability;

      if (visible) {
        servicesObj = {
          ...servicesObj,
          [platformName]: ability,
        };
      }
    });

    let checkShufflePublish = true;

    Object.values(servicesObj)
      .filter(
        (service) =>
          !service.platform.name.startsWith(CONST_SHUFFLE) ||
          service.platform.name === 'SHUFFLE_PUBLISH',
      )
      .forEach((service) => {
        const {
          platform: { name: platformName },
        } = service;

        if (
          platformName === 'PUBLISH' ||
          platformName === 'SELFPUBLISH' ||
          platformName === 'DISTRIBUTION' ||
          platformName === 'POS' ||
          platformName === 'SHUFFLE_PUBLISH'
        ) {
          if (checkShufflePublish) {
            if (servicesObj.SHUFFLE_PUBLISH && servicesObj.SHUFFLE_PUBLISH.enabled) {
              checkShufflePublish = false;

              services.push({
                ...servicesObj.SHUFFLE_PUBLISH,
                enabled: false,
                customerType: customer_type,
                isNotSignable: !customer_type || !mandatoryStepsFulfilled,
                valid_from: servicesObj.SHUFFLE_PUBLISH.valid_from
                  ? moment(servicesObj.SHUFFLE_PUBLISH.valid_from).format('L')
                  : '',
              });
            } else if (servicesObj.DISTRIBUTION && servicesObj.DISTRIBUTION.enabled) {
              checkShufflePublish = false;

              services.push({
                ...servicesObj.DISTRIBUTION,
                customerType: customer_type,
                isNotSignable: !customer_type || !mandatoryStepsFulfilled,
                valid_from: servicesObj.DISTRIBUTION.valid_from
                  ? moment(servicesObj.DISTRIBUTION.valid_from).format('L')
                  : '',
              });
            } else if (servicesObj.PUBLISH && servicesObj.PUBLISH.enabled) {
              checkShufflePublish = false;

              services.push({
                ...servicesObj.PUBLISH,
                customerType: customer_type,
                isNotSignable: !customer_type || !mandatoryStepsFulfilled,
                valid_from: servicesObj.PUBLISH.valid_from
                  ? moment(servicesObj.PUBLISH.valid_from).format('L')
                  : '',
              });
            } else if (servicesObj.SELFPUBLISH && servicesObj.SELFPUBLISH.enabled) {
              checkShufflePublish = false;

              services.push({
                ...servicesObj.SELFPUBLISH,
                customerType: customer_type,
                isNotSignable: !customer_type || !mandatoryStepsFulfilled,
                valid_from: servicesObj.SELFPUBLISH.valid_from
                  ? moment(servicesObj.SELFPUBLISH.valid_from).format('L')
                  : '',
              });
            }
          }
        } else {
          services.push({
            ...service,
            valid_from: service.valid_from ? moment(service.valid_from).format('L') : '',
          });
        }
      });

    services.unshift({
      agreement_url: `https://streetlib-agreements.s3.eu-west-1.amazonaws.com/${
        languageCode === 'it' ? 'IT' : 'EN'
      }_StreetLib_Legal-Policy_202501.pdf`,
      enabled: dashAgreement.fulfilled,
      platform: {
        label: 'StreetLib',
        description: translations[languageCode].streetlib_description,
        url: '/home',
        name: 'DASH',
      },
    });

    if (servicesObj.SHUFFLE_WRITE && servicesObj.SHUFFLE_WRITE.enabled) {
      services.push({
        ...servicesObj.SHUFFLE_WRITE,
        enabled: false,
        valid_from: servicesObj.SHUFFLE_WRITE.valid_from
          ? moment(servicesObj.SHUFFLE_WRITE.valid_from).format('L')
          : '',
      });
    }

    if (company?.code === 'SL_IT' && country?.alpha2 === 'IT') {
      services.push({
        platform: {
          label: 'Ready',
          description: translations[languageCode].streetlib_ready_description,
          url: 'https://readybooks.io',
        },
        enabled: true,
        isNotSignable: false,
      });
    }

    // Evita doppio tile quando il backend restituisce più ability con lo stesso label (es. Community)
    const seenLabels = new Set();
    const dedupedServices = services.filter((s) => {
      const label = s.platform?.label;
      if (!label) return true;
      if (seenLabels.has(label)) return false;
      seenLabels.add(label);
      return true;
    });

    return { services: dedupedServices, isWaiting: isGetWaiting };
  },
);

const selectProfile = createSelector(
  (state) => state.profile,
  configSelector,
  (state) => state.auth.adminRequestedStreetlibInternalId,
  (profile, { languageCode }, adminRequestedStreetlibInternalId) => {
    const { localAuth, payload } = profile;

    let completitionPerc = 0;
    let isAdmin = adminRequestedStreetlibInternalId !== '';
    let isAdminDe = false;
    let isAdminAnalytics = false;
    let isEnabled = true;
    let tokenEmail = null;
    let isEmailUpdated = false;
    let additionalSteps = null;
    let mandatorySteps = null;
    let mandatoryStepsFulfilled = true;
    let needsNewAgreement = false;
    let needsNewLegalPolicy = false;
    let redirectPath = null;
    let onboardComplete = false; // Initialize onboardComplete
    let needsPayment = false;
    const { guid } = payload;
    const customerType = payload.customer_type;
    // Cosa questo account puo' comprare oggi e da quale cassa. Lo decide dash-api da
    // una data sola, cosi' qui non c'e' nessuna logica di date e non serve una build
    // per spostarla. Assente = vecchio comportamento, che e' la direzione sicura.
    const offer = payload.offer || null;
    // Check for any active plan that grants PRO and hasn't expired.
    //
    // Which plans grant PRO is said by the catalogue, in plan.grants_pro, and no
    // longer read from the letters of the code: StreetLib One is SL-ONE, there is no
    // "PRO" in it, and the rule this replaces would have shown its buyers the upsell
    // they had just paid to remove. The code list is kept only for a payload coming
    // from a dash-api that predates the flag.
    const PRO_PLAN_CODES = ['PRO-SUB', 'US-PRO', 'PRO-LIFE', 'SL-ONE'];
    const hasPROPlan = payload.active_plans?.some((plan) => {
      if (plan.status !== 'active') return false;

      const grantsPRO =
        plan.grants_pro !== undefined && plan.grants_pro !== null
          ? plan.grants_pro
          : PRO_PLAN_CODES.includes(plan.code);
      if (!grantsPRO) return false;

      // Check if plan hasn't expired
      if (plan.expire_on) {
        const expireDate = moment(plan.expire_on);
        const today = moment().startOf('day');
        if (expireDate.isBefore(today)) return false; // Plan has expired
      }

      return true; // Plan is active, grants PRO, and hasn't expired
    });

    if (payload.abilities) {
      const { abilities } = payload;

      const dashAbility = abilities.find((ability) => ability.platform?.name === 'DASH');

      isAdminAnalytics = dashAbility.role.toLowerCase() === 'admin';

      if (!isAdmin) {
        if (dashAbility) {
          isAdmin = dashAbility.role.toLowerCase() === 'admin';
          isAdminDe = dashAbility.roles.includes('ADMIN_DE');
          isEnabled = dashAbility.enabled;
        }

        const distributionAbility = abilities.find(
          (ability) => ability.platform?.name === 'DISTRIBUTION',
        );

        const shufflePublishAbility = abilities.find(
          (ability) => ability.platform?.name === 'SHUFFLE_PUBLISH',
        );

        if (
          distributionAbility &&
          shufflePublishAbility &&
          !distributionAbility.enabled &&
          !distributionAbility.visible &&
          distributionAbility.user_guid &&
          shufflePublishAbility.enabled &&
          shufflePublishAbility.visible
        ) {
          needsNewAgreement = true;
        }
      }
    }

    if (payload.tokens) {
      const { tokens } = payload;
      tokenEmail = tokens.find((token) => token.action === 'user_update');
      isEmailUpdated = !!tokenEmail;
    }

    if (payload.onboard) {
      const {
        steps,
        completition: { current },
      } = payload.onboard;

      completitionPerc = current;

      const dashAgreement = _.find(steps, { name: 'dash_accepted_agreement' });
      const proSubscriptionStep = _.find(steps, { name: 'dash_pro_subscription' });

      // Onboarding complete when all mandatory steps from API are fulfilled
      const mandatoryStepsList = steps.filter((step) => step.requirement === 'mandatory');
      onboardComplete =
        mandatoryStepsList.length > 0 &&
        mandatoryStepsList.every((step) => step.fulfilled);

      // Needs payment when API returns mandatory step dash_pro_subscription and it's not fulfilled
      needsPayment = !!(
        proSubscriptionStep &&
        proSubscriptionStep.requirement === 'mandatory' &&
        !proSubscriptionStep.fulfilled
      );


      // Check if user is not admin and has not completed onboarding
      if (
        !isAdmin &&
        !isAdminDe &&
        !onboardComplete &&
        ![
          '/profile',
          '/services',
          '/enable-service',
          '/billing/profile',
          '/billing/payment-account',
          '/billing/summary-billing-profile',
          '/billing/summary-payment-account',
          '/billing/profiles',
          '/onboarding',
        ].includes(window.location.pathname)
      ) {
        redirectPath = '/onboarding';
      }

      if (needsNewAgreement) {
        redirectPath = '/enable-service?id=SHUFFLE_PUBLISH';
      }

      if (!dashAgreement.fulfilled) {
        needsNewLegalPolicy = true;
        redirectPath = '/enable-service?id=DASH';
      }

      // SET mandatory steps
      steps
        .filter((step) => step.requirement === 'mandatory')
        .forEach((step) => {
          if (!step.fulfilled) {
            mandatoryStepsFulfilled = false;
          }

          const stepWithAgreement = { ...step };
          if (stepWithAgreement.name === 'dash_accepted_agreement') {
            stepWithAgreement.agreement = `https://streetlib-agreements.s3.eu-west-1.amazonaws.com/${
              languageCode === 'it' ? 'IT' : 'EN'
            }_StreetLib_Legal-Policy_202501.pdf`;
          }

          mandatorySteps = {
            ...mandatorySteps,
            [stepWithAgreement.name]: stepWithAgreement,
          };
        });

      // SET additional steps
      steps
        .filter((step) => step.requirement === 'optional')
        .forEach((step) => {
          additionalSteps = {
            ...additionalSteps,
            [step.name]: step,
          };
        });
    }

    const result = {
      localAuth,
      payload: {
        ...payload,
        avatar: payload.avatar,
        country_id: payload.country ? payload.country.id : '',
        hasPROPlan,
      },
      guid,
      customerType,
      isAdmin,
      isAdminDe,
      isAdminAnalytics,
      isEnabled,
      isEmailUpdated: !!isEmailUpdated,
      newEmail: tokenEmail ? tokenEmail.data.email : null,
      completitionPerc,
      additionalSteps,
      mandatorySteps,
      mandatoryStepsFulfilled,
      needsNewAgreement,
      needsNewLegalPolicy,
      redirectPath,
      onboardComplete,
      needsPayment,
      offer,
    };


    return result;
  },
);

export default selectProfile;
